import { addOnMinutesOf, defaultItemOption } from '@yourstore/domain';
import { gridStarts, intersect, maxDepthWithin, merge, overlaps, saturated, subtract, } from './interval.js';
import { buffersFor, effectiveOfferPolicy, judgeCandidate, offerFloorMs, protectedIntervalsOn, relaxationLadder, } from './offers.js';
import { addDays, dayDistance, dayTimeToUtc, eachDay, toIsoDay, toIsoUtc, weekdayIndex, } from './timezone.js';
/**
 * Do the house's offer rules apply to this ask?
 *
 * One function, because three files ask it and a fourth reading of an absent
 * field would be a fourth policy. See `SlotQuery.house_rules` for why absence
 * resolves the way it does.
 */
export function houseRulesApply(query) {
    if (query.house_rules !== undefined)
        return query.house_rules === 'apply';
    return query.audience === 'partner';
}
const MINUTE = 60_000;
/**
 * How long this service occupies a table for **this** party and **this** guest
 * — spec §4.6's features 3 and 5, and the one place that decides it.
 *
 * The resolution order, and why it is this way round:
 *
 *   1. **`override`** — a length somebody *stated* about this booking. Two
 *      sources feed it and they resolve outside this function, in that order:
 *      an explicit per-booking length a member of staff typed into the sheet,
 *      then the client's remembered length for this service
 *      (`SchedulingContext.duration_overrides`). The caller passes whichever it
 *      has, which is why this is one parameter and not two — both are the same
 *      kind of fact ("this appointment is N minutes"), and the only difference
 *      between them is which of the two the caller found first;
 *   2. the first ladder rung whose `max_party` covers the party — the specific
 *      answer the owner wrote down for a two-top;
 *   3. `turn_minutes`, the house-wide turn — the general answer;
 *   4. **the chosen option's `duration_min`**, when it has one — the 90 of
 *      "one facial at 30 · 60 · 90 · 120". It **replaces `duration_min` as the
 *      base**: it slots in where the base is rather than beside it, so rungs
 *      1–3 are untouched and every property this file already carried stays
 *      green. An option whose `duration_min` is `null` — a dish's *Large* —
 *      changes the price and not the span, and falls through to;
 *   5. `duration_min` — what the item says it is, which is the only thing a spa
 *      has and therefore the only thing that may change for a spa: nothing.
 *
 * **An `override` still outranks an option, and which override it is has been
 * decided before this function is called.** `client_service_durations` may name
 * an option or may say "however it is sold"; the caller reads the option's
 * number first and the service's second (`candidatesForService`), for the same
 * reason this takes one `override` parameter rather than two — both are the same
 * kind of fact, and the only difference is which of them the caller found first.
 * A remembered length is a statement about *this person and this thing she
 * buys*, and it is more specific than the menu's number for that length.
 *
 * **Add-on minutes are not here.** They *add* rather than replace, so they are
 * applied outside this resolver — `span = effectiveDurationMin(…) + Σ
 * add_on.duration_min` — and keeping the two operations apart is most of what
 * keeps the arithmetic honest on both sides of the wire (design §3).
 *
 * **An override skips the ladder deliberately, and that is not an oversight.** A
 * remembered length is a statement about *this client*: "Maria's brows take
 * thirty." The ladder is a statement about *a party of six*. When the owner has
 * told us how long this person takes, that is more specific than any rule about
 * headcount, and averaging the two — or letting a rung outrank the name — would
 * quietly give the answer the owner explicitly did not write down. The two
 * facts have never yet met in one tenant (a spa has no ladder, a restaurant
 * does not remember a diner's turn), so the precedence is a decision made now
 * rather than a behaviour discovered later.
 *
 * The resolved number **replaces** `duration_min`; it is not added to it. That
 * is the difference between "a six-top turns in 120 minutes" and "a six-top
 * turns in 90 plus another 120", and only the first is a sentence about a
 * restaurant. It follows that the slot the guest is offered *ends* when the
 * table frees: the sold span and the turn are the same span, buffers excepted,
 * so a booking made from an offered slot writes exactly the occupancy the
 * engine reserved and `appointment_resources` cannot disagree with what was
 * advertised.
 *
 * **Buffers are untouched by an override.** `buffer_before_min` and
 * `buffer_after_min` still wrap whatever span this resolves to — the owner's
 * "I do a half an hour between clients" is about the gap, not about the
 * treatment, and a fifteen-minute wax and a thirty-five-minute wax each get
 * their half hour.
 *
 * `party_size` absent means "not asked on behalf of a party" — direct callers of
 * `slotsForService` in tests, mainly — and takes rungs out of play rather than
 * guessing a party of one, because guessing would make a two-top's turn the
 * default answer to a question nobody asked. A non-positive `override` is read
 * as no override rather than thrown at: the bounds are the store's CHECK and
 * the repository's refusal, and a pure resolver is the wrong place to discover
 * that a row is impossible.
 */
export function effectiveDurationMin(service, partySize, override, 
/** The option chosen for this ask, when the service is sold at more than one. */
option) {
    if (typeof override === 'number' && override > 0)
        return override;
    const rungs = service.turn_by_party_size ?? [];
    if (partySize !== undefined && rungs.length > 0) {
        const match = [...rungs]
            .sort((a, b) => a.max_party - b.max_party)
            .find((rung) => partySize <= rung.max_party);
        if (match !== undefined)
            return match.duration_min;
    }
    const flat = service.turn_minutes;
    if (typeof flat === 'number' && flat > 0)
        return flat;
    // The base, which is the option's when one was chosen and it states a span.
    // A non-positive number is read as "no span stated" for `override`'s reason:
    // the bounds are the store's CHECK, and a pure resolver is the wrong place to
    // discover that a row is impossible.
    const fromOption = option?.duration_min;
    if (typeof fromOption === 'number' && fromOption > 0)
        return fromOption;
    return service.duration_min;
}
/* ------------------------------------------------------------------ */
/* Options and add-ons — resolution, once (docs/catalog-options-design.md) */
/* ------------------------------------------------------------------ */
/**
 * The option this ask means: the one it named, or — when it named none and the
 * service has options — **the default**.
 *
 * `undefined` for a service with no live options, which is the whole fleet and
 * is what makes an absent field byte-identical. `null` means the ask named an
 * option this service does not have, which is a refusal rather than a fallback:
 * quietly substituting the default would offer a guest ninety minutes and sell
 * her thirty.
 */
export function resolveOption(service, optionId) {
    const options = (service.options ?? []).filter((o) => o.active);
    if (optionId === undefined)
        return defaultItemOption(options) ?? undefined;
    return options.find((o) => o.id === optionId) ?? null;
}
/** The live add-on links this ask named, in the catalog's own order. */
export function resolveAddOns(service, addOnIds) {
    if (addOnIds === undefined || addOnIds.length === 0)
        return [];
    const wanted = new Set(addOnIds);
    return (service.add_ons ?? []).filter((a) => a.active && wanted.has(a.item_id));
}
/**
 * Ids the ask attached that this parent does not offer — what a route turns into
 * `400 invalid_request` (design §4.3 rule 4, the closed error set unchanged).
 *
 * Exported because the engine must not be the thing that decides an HTTP status
 * and a route must not be the thing that decides what "linked to this parent"
 * means. Both facts, one function.
 */
export function unknownAddOnIds(service, addOnIds) {
    if (addOnIds === undefined || addOnIds.length === 0)
        return [];
    const live = new Set((service.add_ons ?? []).filter((a) => a.active).map((a) => a.item_id));
    return [...new Set(addOnIds)].filter((id) => !live.has(id)).sort();
}
/**
 * The minutes these add-ons add to the span — summed **once**, before the grid
 * slicer runs (design §4.6: no per-slot work is added, the slicer is
 * byte-identical with a different integer).
 */
export function addOnMinutes(service, addOnIds) {
    return addOnMinutesOf(resolveAddOns(service, addOnIds));
}
export function serviceRefusalFor(service, query) {
    if (!service.active)
        return 'inactive';
    if (query.audience === 'partner' && !service.bookable_by_partner) {
        return 'not_bookable_by_partner';
    }
    // Never offered on its own, and told so by name. The row is still on the
    // catalog and still inside its parents' entries; it simply has no standalone
    // answer to give.
    if (service.attach_only === true)
        return 'attach_only';
    if (service.max_party < query.party_size)
        return 'party_too_large';
    if (resolveOption(service, query.option_id) === null)
        return 'no_such_option';
    if (unknownAddOnIds(service, query.add_on_ids).length > 0)
        return 'no_such_add_on';
    return null;
}
/**
 * The same question about the **one service an ask named** — `null` when the ask
 * named none, or when the service it named can answer.
 *
 * This is the function a surface calls to turn "no slots" into a sentence: an
 * attach-only treatment asked for alone, an option that belongs to another
 * service, an add-on that is not linked to this parent.
 */
export function namedServiceRefusal(ctx, query) {
    if (query.service_id === undefined)
        return null;
    const service = ctx.services.find((s) => s.id === query.service_id);
    if (service === undefined)
        return 'no_such_service';
    return serviceRefusalFor(service, query);
}
/**
 * Covers already starting in the grid step that begins at `start`.
 *
 * The step is the half-open `[start, start + grid)`. Offered starts are
 * grid-aligned to their window's opening and every booking this system made
 * began at one, so a committed start normally lands exactly on `start`; an
 * imported booking that begins off-grid counts toward the step it falls inside.
 * A booking that begins *before* the first offered start of its step counts
 * toward no step, which under-counts by at most one party at the edge of an
 * import and is the one direction worth erring in: pacing exists to stop us
 * promising too much, not to stop us offering anything at all.
 */
function coversStartingIn(sorted, start, stepMs) {
    let covers = 0;
    for (const committed of sorted) {
        if (committed.start >= start + stepMs)
            break;
        if (committed.start >= start)
            covers += committed.covers;
    }
    return covers;
}
/* ------------------------------------------------------------------ */
/* Resource roles — a service that needs a person AND a room (§4.1.2)   */
/* ------------------------------------------------------------------ */
/**
 * **The most lanes a roled walk will consider**, people and rooms together —
 * §5.5.5's "resources scanned per request: 25", applied to the one walk whose
 * cost is a product rather than a sum. People are kept first (a person is the
 * primary lane and the thing a guest asks for by name) and rooms fill the
 * remainder, each side keeping at least one, so a 20-person team with 10 rooms
 * is walked as 20 × 5 and never as 200 pairs per start.
 */
export const MAX_ROLED_LANES = 25;
/**
 * **A hard stop on candidates generated by one roled walk** — §5.5.5's
 * "candidate slots computed: 5 000", enforced inside the engine and not as a
 * `LIMIT` on the output. The union walk is bounded by resources × grid × days
 * and the route's 31-day/25-resource clamps; the cross product multiplies
 * lanes instead of adding them, so it carries its own stop. `roles.test.ts`
 * measures an 8 × 8 team against it.
 */
export const CANDIDATE_HARD_STOP = 5_000;
/**
 * **Partition a service's resources by role** — the one reading of
 * `resource_roles`, shared by the slicer below and by the store's lane
 * resolution (`resolveLanes`), so the engine and the booking write cannot
 * disagree about what an item requires.
 *
 * `null` means *one lane*: every role is `'any'`, or only one side is named
 * (one of {people} alone is the union over the people, which the caller's
 * `resource_ids` already are). Both sides non-empty means *one of each*.
 *
 * The `'any'` rule: an `'any'` resource joins **whichever side is empty**;
 * when both sides are named it joins neither. Sorted by id on both sides so the
 * walk, and therefore the answer, is deterministic.
 */
export function roleSides(resourceIds, roles) {
    if (roles === undefined)
        return null;
    const people = [];
    const rooms = [];
    const any = [];
    for (const id of resourceIds) {
        const role = roles[id] ?? 'any';
        if (role === 'person')
            people.push(id);
        else if (role === 'room')
            rooms.push(id);
        else
            any.push(id);
    }
    if (people.length === 0 && rooms.length === 0)
        return null;
    if (people.length === 0)
        people.push(...any);
    else if (rooms.length === 0)
        rooms.push(...any);
    if (people.length === 0 || rooms.length === 0)
        return null;
    return {
        people: [...new Set(people)].sort((a, b) => a.localeCompare(b)),
        rooms: [...new Set(rooms)].sort((a, b) => a.localeCompare(b)),
    };
}
/** Every lane a slot occupies, primary first. One entry for the whole fleet. */
export function slotLanes(slot) {
    return [slot.resource_id, ...(slot.also_resource_ids ?? [])];
}
/**
 * Services this query is allowed to consider, in a stable order.
 *
 * The predicates live in `serviceRefusalFor`, which is the same rule a route
 * asks *why*; selecting which service was named stays here, because that is the
 * ask narrowing itself rather than the service being unable to answer.
 */
export function candidateServices(ctx, query) {
    return ctx.services
        .filter((s) => serviceRefusalFor(s, query) === null)
        .filter((s) => (query.service_id === undefined ? true : s.id === query.service_id))
        .sort((a, b) => a.id.localeCompare(b.id));
}
/**
 * Open time for one resource on one calendar day, in the tenant's zone.
 *
 * A day is a list of windows — one for a spa, two for a restaurant's lunch and
 * dinner services (spec 4.6). Windows that overlap or touch are merged so the
 * slicer never sees a seam that is not real.
 */
function openOnDay(ctx, resource, isoDay) {
    const day = resource.hours[weekdayIndex(isoDay)];
    if (day === undefined || day.closed)
        return [];
    const windows = day.windows
        .map((w) => ({
        start: dayTimeToUtc(ctx.timezone, isoDay, w.start),
        end: dayTimeToUtc(ctx.timezone, isoDay, w.end),
    }))
        // An `end` at or before `start` is a misconfigured window, not an overnight
        // shift. Overnight hours are a real thing but not one any current tenant
        // has; a silent 23-hour window would be far worse than treating it as closed.
        .filter((w) => w.end > w.start);
    return merge(windows);
}
function blackoutsFor(ctx, resourceId) {
    return ctx.blackouts
        .filter((b) => b.resource_id === null || b.resource_id === resourceId)
        .map((b) => ({ start: b.start, end: b.end }));
}
function busyFor(ctx, resourceId) {
    return ctx.busy
        .filter((b) => b.resource_id === resourceId)
        .map((b) => ({ start: b.start, end: b.end }));
}
/**
 * Every slot for one service across a set of days.
 *
 * A slot is offered for its **effective duration** — `duration_min`, the turn
 * time this party earns, or the length this *guest* is remembered as taking
 * (spec §4.6) — but the resource is occupied for
 * `buffer_before + effective + buffer_after`: a 60-minute massage takes 75
 * minutes of the room and is sold as 60.
 *
 * When `ctx.duration_overrides` names this service, that number is the span,
 * and it is the span **everywhere**: the offered slot, the block the grid steps
 * by, the lead-in, the depth a pool is measured to. Which is the point — the
 * owner who says "Susie's brow wax takes me thirty-five" is told about a
 * thirty-five-minute opening, and the appointment that lands writes exactly the
 * occupancy this function reserved.
 *
 * `partySize` is optional and only two things depend on it: which rung of the
 * turn ladder applies, and whether a pool's units are big enough to seat the
 * party at all. A party of six is not offered a two-top, which is the same
 * sentence as "a slot's `capacity` carries what fits" read from the other side.
 *
 * ## Single lane and pool are two different walks (v1.8 §2)
 *
 * A resource with `units = 1` is **packed**: starts step by the occupied block,
 * because a second offer overlapping the first could not be booked alongside
 * it. That is every spa, and the walk is unchanged to the byte.
 *
 * A resource with `units > 1` steps on the **tenant's grid** instead, and each
 * candidate is admitted only if the lanes it would need are free for its whole
 * span: at every instant, bookings already on the pool plus offers already made
 * plus this one never exceed `units`. Twelve free tables and a 90-minute turn
 * on a quarter-hour grid is six offers deep and all of them are honoured — a
 * restaurant offers a seating every fifteen minutes. With one lane free the
 * admission degrades to exactly the packed walk, which is the proof that this
 * is a generalization rather than a second rule.
 *
 * Greedy, earliest first. It is not the offer set of maximum cardinality and
 * does not try to be: it is deterministic, and every subset of it is bookable,
 * which are the two properties the agreement actually asks for.
 */
export function slotsForService(ctx, service, days, cap, partySize, houseRules = false, resourceId, 
/** The option chosen and the add-ons attached. Absent is the whole fleet. */
ask) {
    return candidatesForService(ctx, service, days, cap, partySize, houseRules, resourceId, ask).map((c) => c.slot);
}
export function candidatesForService(ctx, service, days, cap, partySize, houseRules = false, 
/** `SlotQuery.resource_id` — "with Maria". Absent is "Anyone". */
resourceId, 
/** `SlotQuery`'s option and add-ons. Absent is the whole fleet. */
ask) {
    if (cap <= 0)
        return [];
    // The option this ask means — the one it named, or the default when it named
    // none and this service has options. `null` is an option that is not this
    // service's, and the honest answer to that is nothing at all: substituting
    // the default would offer a span nobody asked for. An attach-only service
    // has no standalone answer either, for its own reason.
    const option = resolveOption(service, ask?.option_id);
    if (option === null)
        return [];
    if (service.attach_only === true)
        return [];
    if (unknownAddOnIds(service, ask?.add_on_ids).length > 0)
        return [];
    // The asking client's remembered length for this service, if the caller built
    // a context on somebody's behalf. Read here and nowhere else: every span below
    // — the offered one, the occupied block, the lead-in — falls out of this one
    // number, so an offer, the lane it claims and the exclusivity it is judged by
    // cannot disagree about how long the appointment is.
    //
    // **Her length for *this option* first**, then her length for the service
    // however it is sold. A 75-minute memory on "Custom Deluxe Facial" must not
    // be handed to the guest who chose the 30, which is exactly why the store's
    // key widened to carry the option.
    const override = (option === undefined ? undefined : ctx.option_duration_overrides?.[option.id]) ??
        ctx.duration_overrides?.[service.id];
    // The option **replaces** the base; the add-ons' minutes **add**. Summed once,
    // here, before the grid slicer runs — so the slicer below is byte-identical
    // with a different integer and no per-slot work is bought (design §4.6).
    const effectiveMin = effectiveDurationMin(service, partySize, override, option) +
        addOnMinutes(service, ask?.add_on_ids);
    // The buffers, from the one resolver: this item's own, or the house's gap
    // when this item has none (A2). Absent defaults hand back the service's own
    // two numbers, which is what keeps this walk byte-identical for the fleet.
    const buffers = buffersFor(ctx, service);
    const durationMs = effectiveMin * MINUTE;
    const blockMs = (buffers.before_min + effectiveMin + buffers.after_min) * MINUTE;
    const leadInMs = buffers.before_min * MINUTE;
    const gridMs = ctx.slot_grid_min * MINUTE;
    const seatsNeeded = partySize ?? 1;
    // A3: the earliest instant this ask may be offered. `ctx.now` when the house's
    // rules do not apply, which is every direct caller of this function and every
    // staff surface — so the "never the past" floor is unchanged where nothing
    // was configured and unchanged for the owner's own hand regardless.
    const floorMs = offerFloorMs(ctx, houseRules);
    const configuredAnchors = houseRules ? (ctx.offer_policy?.anchors ?? []) : [];
    const resources = ctx.resources.filter((r) => r.active &&
        service.resource_ids.includes(r.id) &&
        r.capacity >= seatsNeeded &&
        // "With Maria" (S2). Absent is "Anyone", and this predicate is then
        // constantly true, which is what makes the unnamed walk byte-identical.
        (resourceId === undefined || r.id === resourceId));
    // **A service that needs a person AND a room** (spec §4.1.2) takes the
    // cross-product walk below, and nothing else does: `roleSides` is `null` for
    // every service whose links are all `'any'`, which is the fleet, and the
    // union walk that follows is the code path it has always been — kept rather
    // than re-derived, so "absent is byte-identical" is a property of the branch
    // and not of a refactor. The sides are read off the service's own links,
    // not the filtered list, so "with Maria" narrows a side rather than turning
    // a roled item into an unroled one.
    const sides = roleSides(service.resource_ids, service.resource_roles);
    if (sides !== null) {
        return crossProductCandidates(ctx, service, days, cap, sides, resourceId, {
            resources: ctx.resources.filter((r) => r.active && service.resource_ids.includes(r.id) && r.capacity >= seatsNeeded),
            durationMs,
            blockMs,
            leadInMs,
            gridMs,
            floorMs,
            configuredAnchors,
            houseRules,
        });
    }
    const out = [];
    // Offers already made on a pool, so the next candidate can be measured
    // against them. Keyed by resource and carried across days and windows,
    // because a lane is a lane whichever window it was claimed in.
    const offeredOn = new Map();
    for (const isoDay of days) {
        // A4: her lunch. Blackout-shaped and public-only — a member of staff may
        // still put somebody in it, which is the whole reason it is not a blackout
        // row (`protectedIntervalsOn`).
        const protectedToday = protectedIntervalsOn(ctx, isoDay, houseRules);
        const anchorsToday = configuredAnchors.map((hhmm) => dayTimeToUtc(ctx.timezone, isoDay, hhmm));
        for (const resource of resources) {
            const open = openOnDay(ctx, resource, isoDay);
            if (open.length === 0)
                continue;
            const units = resource.units ?? 1;
            const pooled = units > 1;
            const busy = busyFor(ctx, resource.id);
            // "Nothing booked on this resource today", which is what switches
            // `touching` over to its anchors. Bookings only: a closure is not a
            // booking, and a day she has blacked out is not a day she has filled.
            const dayOpen = { start: open[0]?.start ?? 0, end: open[open.length - 1]?.end ?? 0 };
            const dayEmpty = !busy.some((b) => overlaps(b, dayOpen));
            // A pool is unavailable only where every lane is taken; a blackout closes
            // the whole pool however many lanes it has. `units = 1` makes the first
            // of those a plain `merge`, which is what a spa has always run.
            const unavailable = merge([
                ...saturated(busy, units),
                ...blackoutsFor(ctx, resource.id),
                ...protectedToday,
            ]);
            // Free time and the grid anchor are per open window: the dinner service
            // aligns to 17:30, not to an offset carried over from lunch — but a free
            // chunk *after a booking inside a window* still aligns to that window's
            // opening, so slots stay on the tenant's grid.
            for (const openWindow of open) {
                const free = subtract([openWindow], unavailable);
                const anchor = openWindow.start;
                // Collected per open window, because the default anchors are "the
                // window's opening and the latest start that fits" and only the whole
                // window's candidate list knows which those are.
                const inWindow = [];
                for (const window of free) {
                    // `after` maps "never offer a time we will not honour" onto the block
                    // start: the offered start is `blockStart + leadIn`, so a block
                    // starting at or before `floor - leadIn` is out. The floor is
                    // `ctx.now` unless the house set a lead time or a cut-off (A3).
                    for (const blockStart of gridStarts(window, blockMs, gridMs, anchor, floorMs - leadInMs, pooled ? 'grid' : 'block')) {
                        const start = blockStart + leadInMs;
                        // The free chunk's own edges are the neighbours: either an occupied
                        // span, a closure, a protected window, or the edge of the day.
                        const geometry = {
                            gap_before_ms: blockStart - window.start,
                            gap_after_ms: window.end - (blockStart + blockMs),
                            grid_ms: gridMs,
                            anchor: anchorsToday.includes(start),
                            day_empty: dayEmpty,
                            pooled,
                        };
                        if (pooled) {
                            // The whole occupied block, buffers included: a lane is held for
                            // the bussing too, so that is the span the depth is measured over.
                            const span = { start: blockStart, end: blockStart + blockMs };
                            const claimed = offeredOn.get(resource.id) ?? [];
                            if (maxDepthWithin(span, [...busy, ...claimed]) + 1 > units)
                                continue;
                            claimed.push(span);
                            offeredOn.set(resource.id, claimed);
                            inWindow.push({
                                slot: {
                                    start: toIsoUtc(start),
                                    end: toIsoUtc(start + durationMs),
                                    resource_id: resource.id,
                                    service_id: service.id,
                                    capacity: resource.capacity,
                                    // Lanes free for the *whole* span — what a second booking at
                                    // this start would find, and the number the partner bounds the
                                    // overlapping offer set by (v1.8 §3).
                                    units_free: units - maxDepthWithin(span, busy),
                                },
                                geometry,
                            });
                            continue;
                        }
                        inWindow.push({
                            slot: {
                                start: toIsoUtc(start),
                                end: toIsoUtc(start + durationMs),
                                resource_id: resource.id,
                                service_id: service.id,
                                capacity: resource.capacity,
                            },
                            geometry,
                        });
                    }
                }
                // The default anchors, when the tenant named none: the first and the
                // last candidate this service has in this window — "the window's
                // opening, and the latest start that fits", read off the walk that
                // actually produced them rather than recomputed from the clock, because
                // the packed walk is what decides which starts exist at all.
                //
                // **Only on a resource-day with nothing booked on it**, which is the
                // only day the anchors are for. Marking them on a busy day would be
                // harmless to `touching` (which ignores them there) and wrong for
                // `rank: 'packing'`, where "is an anchor" is a rung of the score: the
                // last start of a full Thursday would outrank the ones that close a
                // hole, for no reason anybody could point at.
                if (dayEmpty && configuredAnchors.length === 0 && inWindow.length > 0) {
                    markAnchor(inWindow[0]);
                    markAnchor(inWindow[inWindow.length - 1]);
                }
                out.push(...inWindow);
            }
        }
    }
    out.sort((a, b) => a.slot.start.localeCompare(b.slot.start) ||
        a.slot.resource_id.localeCompare(b.slot.resource_id));
    return out.slice(0, cap);
}
function markAnchor(candidate) {
    if (candidate !== undefined)
        candidate.geometry.anchor = true;
}
/**
 * **The cross-product walk** (spec §4.1.2): one candidate per *(person, room)*
 * combination whose two lanes are both free for the whole occupied span.
 *
 * ## What it costs, and where the bound is
 *
 * The union walk's work is a *sum* — resources × starts. This one's is a
 * *product*: **people × rooms candidates per grid start**, per day. Eight
 * therapists and eight rooms on a quarter-hour grid over a ten-hour day is
 * 64 × 40 = 2 560 candidates before the ledger prunes any of them, and a
 * week of that would be 17 920. So two bounds live here and nowhere else:
 * `MAX_ROLED_LANES` caps the lanes considered at §5.5.5's 25 (people first,
 * rooms fill the remainder), and `CANDIDATE_HARD_STOP` ends the walk at
 * §5.5.5's 5 000 candidates however many days were asked about — the earliest
 * days answer, which is the honest partial. `roles.test.ts` measures 8 × 8
 * against both rather than trusting this paragraph.
 *
 * ## What is shared with the union walk, and what is not
 *
 * The span arithmetic — option, add-ons, remembered length, buffers, floor —
 * is resolved once in `candidatesForService` and arrives here as
 * `AskGeometry`; this function never re-derives a length. Open time is the
 * **intersection** of the two lanes' days, unavailability is the **union** of
 * both lanes' busy time, closures and the house's protected windows, and the
 * grid is anchored per open window exactly as the union walk anchors it. A
 * pooled lane on either side (`units > 1`) switches the pair to grid stepping
 * with the same depth test the union walk applies, so a pool of three rooms
 * with one therapist is offered one seating per start and never three.
 *
 * Within one pair the walk is packed, so a pair's own candidates never
 * overlap. Across pairs that share a lane they do — Maria × Room 1 and
 * Maria × Room 2 both name nine o'clock — and that is deliberate: the
 * **ledger** (`offerLedger`, per lane, every lane) is what admits one of them
 * into the offered set, exactly as it arbitrates two services over one room
 * today. Emitting both is what lets Room 2 answer when Room 1 is busy.
 *
 * `resource_id` is the **person** — the primary lane, so the agreement's shape
 * is unchanged — and `also_resource_ids` is the room. `capacity` is the
 * smaller of the two: a couples room with one therapist seats what the
 * therapist can take.
 */
function crossProductCandidates(ctx, service, days, cap, sides, resourceId, ask) {
    const byId = new Map(ask.resources.map((r) => [r.id, r]));
    // "With Maria" narrows the side she is on and leaves the other whole; a
    // resource on neither side yields nothing, which is the honest answer to
    // "is Maria free for a facial she does not do" — `SlotQuery.resource_id`'s
    // own rule, one lane wider.
    if (resourceId !== undefined &&
        !sides.people.includes(resourceId) &&
        !sides.rooms.includes(resourceId)) {
        return [];
    }
    const narrow = (ids) => ids
        .filter((id) => resourceId === undefined || !ids.includes(resourceId) || id === resourceId)
        .map((id) => byId.get(id))
        .filter((r) => r !== undefined);
    let people = narrow(sides.people);
    let rooms = narrow(sides.rooms);
    if (people.length === 0 || rooms.length === 0)
        return [];
    // §5.5.5's 25, as a product bound. People first, at least one of each.
    if (people.length + rooms.length > MAX_ROLED_LANES) {
        const roomBudget = Math.max(1, Math.min(rooms.length, MAX_ROLED_LANES - people.length));
        rooms = rooms.slice(0, roomBudget);
        people = people.slice(0, MAX_ROLED_LANES - roomBudget);
    }
    const out = [];
    // Offers already made on a *pooled* lane, keyed by lane and carried across
    // days, windows and pairs — a lane is a lane whichever pair claimed it. A
    // single-lane resource needs no entry: overlaps between pairs are the
    // ledger's to arbitrate, not this walk's.
    const offeredOn = new Map();
    let stopped = false;
    for (const isoDay of days) {
        if (stopped)
            break;
        const protectedToday = protectedIntervalsOn(ctx, isoDay, ask.houseRules);
        const anchorsToday = ask.configuredAnchors.map((hhmm) => dayTimeToUtc(ctx.timezone, isoDay, hhmm));
        for (const person of people) {
            if (stopped)
                break;
            const openPerson = openOnDay(ctx, person, isoDay);
            if (openPerson.length === 0)
                continue;
            const busyPerson = busyFor(ctx, person.id);
            const unitsPerson = person.units ?? 1;
            for (const room of rooms) {
                if (stopped)
                    break;
                const open = intersect(openPerson, openOnDay(ctx, room, isoDay));
                if (open.length === 0)
                    continue;
                const busyRoom = busyFor(ctx, room.id);
                const unitsRoom = room.units ?? 1;
                // **The person decides the stepping; either lane can be a pool.** A
                // single-lane person can only ever be offered packed — her nine and
                // her nine-fifteen cannot both be booked, whatever the room — so
                // stepping her on the grid would claim the room pool at every quarter
                // hour for offers the ledger will refuse on *her* lane, and the second
                // therapist would find the pool "full" of phantoms. Packed steps claim
                // the pool once per bookable start, and a pool of two rooms then seats
                // two therapists at ten as it should. A pooled *person* (three
                // interchangeable juniors as one resource) steps on the grid exactly
                // as a pooled table does.
                const pooled = unitsPerson > 1;
                const anyPooled = pooled || unitsRoom > 1;
                const dayOpen = { start: open[0]?.start ?? 0, end: open[open.length - 1]?.end ?? 0 };
                const dayEmpty = !busyPerson.some((b) => overlaps(b, dayOpen)) &&
                    !busyRoom.some((b) => overlaps(b, dayOpen));
                const unavailable = merge([
                    ...saturated(busyPerson, unitsPerson),
                    ...saturated(busyRoom, unitsRoom),
                    ...blackoutsFor(ctx, person.id),
                    ...blackoutsFor(ctx, room.id),
                    ...protectedToday,
                ]);
                for (const openWindow of open) {
                    const free = subtract([openWindow], unavailable);
                    const anchor = openWindow.start;
                    const inWindow = [];
                    for (const window of free) {
                        for (const blockStart of gridStarts(window, ask.blockMs, ask.gridMs, anchor, ask.floorMs - ask.leadInMs, pooled ? 'grid' : 'block')) {
                            const start = blockStart + ask.leadInMs;
                            const span = { start: blockStart, end: blockStart + ask.blockMs };
                            const geometry = {
                                gap_before_ms: blockStart - window.start,
                                gap_after_ms: window.end - (blockStart + ask.blockMs),
                                grid_ms: ask.gridMs,
                                anchor: anchorsToday.includes(start),
                                day_empty: dayEmpty,
                                pooled,
                            };
                            // Both lanes, the pool's depth test on whichever side is pooled.
                            // Checked before either is claimed, so a pair refused on the room
                            // does not leave a phantom claim on the person.
                            let unitsFree;
                            if (anyPooled) {
                                const lanes = [
                                    [person, busyPerson, unitsPerson],
                                    [room, busyRoom, unitsRoom],
                                ];
                                let admissible = true;
                                for (const [lane, busy, units] of lanes) {
                                    if (units <= 1)
                                        continue;
                                    const claimed = offeredOn.get(lane.id) ?? [];
                                    if (maxDepthWithin(span, [...busy, ...claimed]) + 1 > units) {
                                        admissible = false;
                                        break;
                                    }
                                    const laneFree = units - maxDepthWithin(span, busy);
                                    unitsFree = unitsFree === undefined ? laneFree : Math.min(unitsFree, laneFree);
                                }
                                if (!admissible)
                                    continue;
                                for (const [lane, , units] of lanes) {
                                    if (units <= 1)
                                        continue;
                                    const claimed = offeredOn.get(lane.id) ?? [];
                                    claimed.push(span);
                                    offeredOn.set(lane.id, claimed);
                                }
                            }
                            inWindow.push({
                                slot: {
                                    start: toIsoUtc(start),
                                    end: toIsoUtc(start + ask.durationMs),
                                    resource_id: person.id,
                                    also_resource_ids: [room.id],
                                    service_id: service.id,
                                    capacity: Math.min(person.capacity, room.capacity),
                                    ...(unitsFree === undefined ? {} : { units_free: unitsFree }),
                                },
                                geometry,
                            });
                            if (out.length + inWindow.length >= CANDIDATE_HARD_STOP) {
                                stopped = true;
                                break;
                            }
                        }
                        if (stopped)
                            break;
                    }
                    if (dayEmpty && ask.configuredAnchors.length === 0 && inWindow.length > 0) {
                        markAnchor(inWindow[0]);
                        markAnchor(inWindow[inWindow.length - 1]);
                    }
                    out.push(...inWindow);
                    if (stopped)
                        break;
                }
            }
        }
    }
    out.sort((a, b) => a.slot.start.localeCompare(b.slot.start) ||
        a.slot.resource_id.localeCompare(b.slot.resource_id) ||
        slotLanes(a.slot).join(',').localeCompare(slotLanes(b.slot).join(',')));
    return out.slice(0, cap);
}
export function offerLedger(ctx) {
    const byId = new Map(ctx.services.map((s) => [s.id, s]));
    const unitsOf = new Map(ctx.resources.map((r) => [r.id, r.units ?? 1]));
    const busyOf = new Map();
    for (const b of ctx.busy) {
        const spans = busyOf.get(b.resource_id) ?? [];
        spans.push({ start: b.start, end: b.end });
        busyOf.set(b.resource_id, spans);
    }
    const offered = new Map();
    /**
     * Sold span plus buffers — what the exclusion constraint will see.
     *
     * It reads the **slot's own** `start`/`end` rather than re-deriving a length
     * from the service, and that is what makes per-client durations correct here
     * for free: a fifteen-minute brow wax and a thirty-five-minute one are two
     * different spans on the same `service_id`, and this measures the one that
     * was actually offered. Re-deriving from `duration_min` would be a second
     * answer to "how long is this appointment" — which is the bug this whole
     * feature has to avoid, and the shape `assertSoldSpan` exists to catch on the
     * wire. Buffers still come from the service, because a buffer is the house's
     * rule and not the guest's — and since A2 they come from `buffersFor`, which
     * is the same sentence with the house's own default behind it. A service this
     * context has never heard of still contributes nothing, because an unknown
     * item's buffers are unknown rather than zero-by-default.
     */
    const occupiedSpan = (slot) => {
        const svc = byId.get(slot.service_id);
        const buffers = svc === undefined ? { before_min: 0, after_min: 0 } : buffersFor(ctx, svc);
        return {
            start: Date.parse(slot.start) - buffers.before_min * MINUTE,
            end: Date.parse(slot.end) + buffers.after_min * MINUTE,
        };
    };
    // **Every lane the slot occupies**, primary first (§4.1.2). A slot on one
    // lane — the fleet — is a list of one, and the loop below is then the exact
    // check this ledger has always made. A roled slot is admitted only when
    // *each* of its lanes admits it, and recorded on each, so Maria × Room 1 at
    // nine and Sofia × Room 1 at nine cannot both be offered: the room refuses
    // the second, however free Sofia is.
    const record = (slot, span) => {
        for (const lane of slotLanes(slot)) {
            const spans = offered.get(lane);
            if (spans === undefined)
                offered.set(lane, [span]);
            else
                spans.push(span);
        }
    };
    const laneAdmits = (lane, span) => {
        const units = unitsOf.get(lane) ?? 1;
        const already = offered.get(lane) ?? [];
        if (units <= 1)
            return !already.some((s) => overlaps(s, span));
        const busy = busyOf.get(lane) ?? [];
        return maxDepthWithin(span, [...busy, ...already]) + 1 <= units;
    };
    return {
        claim: (slot) => {
            record(slot, occupiedSpan(slot));
        },
        admit: (slot) => {
            const span = occupiedSpan(slot);
            // All lanes tested before any is recorded: a slot refused on its room
            // must not leave a claim on its person.
            if (!slotLanes(slot).every((lane) => laneAdmits(lane, span)))
                return false;
            record(slot, span);
            return true;
        },
    };
}
/**
 * Slots across every candidate service, interleaved chronologically — admitted
 * **per resource across the whole offered set** by the ledger above (agreement
 * §4.2 rule 3, as v1.8 §2 splits it on `units`).
 *
 * Per-resource slicing already produces a jointly bookable set within one
 * service; this is the cross-service half: an "anything bookable" query must
 * not offer the same room at the same instant once per deliverable service, and
 * must not offer a pool more deeply than its lanes. Kept greedily, earliest
 * first (service id breaking ties, so the pick is deterministic), and compared
 * on **occupied spans** — sold span plus buffers — so that any subset of what
 * we offer can actually be booked together. Comparing sold spans alone would
 * satisfy the letter of the rule while offering pairs the exclusion constraint
 * refuses.
 *
 * **Pacing** (spec §4.6, feature 4) is applied here and nowhere else: it is a
 * question about the kitchen rather than about a table, and this is the only
 * function that knows both the whole offered set and the party doing the
 * asking. It is applied **before** the exclusivity check and not after, which is
 * the ordering that matters: a slot the kitchen cannot take must not consume its
 * resource's place in the packed book either, or one paced-out seven o'clock
 * would take the eight o'clock down with it. The consequence is that pacing is
 * not merely a filter — dropping a slot can let a *different* service's
 * candidate onto the freed lane — and that is exactly why rule 3 is enforced by
 * the loop below rather than inherited from the order the candidates arrived in.
 * Both properties are tested against random worlds in `exclusivity.test.ts`.
 *
 * ## The stage order, since A4
 *
 * Three filters run over each candidate, in this order, and the order is a
 * decision rather than an accident:
 *
 *  1. **Pacing** — capacity. The kitchen cannot plate it, so nothing else about
 *     it matters. Unchanged, and first for the reason above.
 *  2. **The offer policy** (`judgeCandidate`) — preference. The house would
 *     rather not name this time.
 *  3. **Exclusivity** (the ledger) — the agreement's rule 3.
 *
 * Both filters sit **before** the ledger for the same reason pacing always did:
 * a candidate nobody will be offered must not consume its resource's place in
 * the packed book. Under `touching`, the 60-minute facial at 11:45 is dropped —
 * and the 15-minute wax at 11:00, which *does* abut the eleven o'clock booking,
 * has to be able to take that lane. If the policy ran after the ledger, the
 * facial would already have claimed it and the wax would be refused for a
 * reason nobody could see.
 *
 * Capacity before preference, and never the other way round: relaxing the
 * policy must never resurrect a slot pacing refused, and it cannot, because
 * pacing is decided first and the ladder only re-runs the second filter.
 *
 * ## The ladder
 *
 * If the configured mode keeps nothing at all while a looser one would have
 * kept something, the looser one answers — `touching → no_slivers → open`, per
 * ask. `policy_applied` on `slotsInWindowWithPolicy` says which rung did, so a
 * caller can tell "this is what she wants offered" from "this is what was left".
 * Bounded: at most three passes over an already-generated candidate list, no
 * new queries, and exactly one pass for every tenant that has configured
 * nothing.
 */
export function slotsInWindow(ctx, query, days, cap) {
    return slotsInWindowWithPolicy(ctx, query, days, cap).slots;
}
export function slotsInWindowWithPolicy(ctx, query, days, cap) {
    const services = candidateServices(ctx, query);
    if (services.length === 0 || days.length === 0) {
        return { slots: [], policy_applied: 'open', relaxed: false, lead_count: 0 };
    }
    const houseRules = houseRulesApply(query);
    const policy = effectiveOfferPolicy(ctx.offer_policy, houseRules);
    const perService = services.flatMap((s) => candidatesForService(ctx, s, days, cap, query.party_size, houseRules, query.resource_id, query));
    perService.sort((a, b) => a.slot.start.localeCompare(b.slot.start) ||
        a.slot.service_id.localeCompare(b.slot.service_id) ||
        a.slot.resource_id.localeCompare(b.slot.resource_id) ||
        // Two combinations sharing a person differ only in the room (§4.1.2);
        // the tie has to be total or the ledger's pick is not deterministic.
        slotLanes(a.slot).join(',').localeCompare(slotLanes(b.slot).join(',')));
    const gapMs = minUsefulGapMin(ctx) * MINUTE;
    const ladder = relaxationLadder(policy.mode);
    // The configured rung first, and a looser one only when the one above kept
    // nothing. Every tenant that has configured nothing runs exactly one pass,
    // under `open`, over the same code path this file has always walked.
    let outcome = applyOfferStages(ctx, query, perService, cap, ladder[0] ?? 'open', gapMs);
    for (const mode of ladder.slice(1)) {
        if (outcome.slots.length > 0)
            break;
        outcome = applyOfferStages(ctx, query, perService, cap, mode, gapMs);
    }
    const ranked = policy.rank === 'packing' ? rankByPacking(outcome) : outcome.slots;
    const top = houseRules
        ? query.audience === 'partner'
            ? policy.top_n?.partner
            : policy.top_n?.guest
        : undefined;
    return {
        slots: ranked,
        policy_applied: outcome.mode,
        relaxed: outcome.mode !== policy.mode,
        lead_count: top === undefined ? ranked.length : Math.min(top, ranked.length),
    };
}
/**
 * **One pass of the three stages, under one mode** — pacing, then the offer
 * policy, then the ledger.
 *
 * Exported because `explainOffers` runs exactly this and prints why, and two
 * implementations of "what would be offered" would be two answers to the one
 * question the simulator exists to settle with the owner. The ledger is
 * constructed fresh on every pass, because it is stateful by design and a rung
 * of the relaxation ladder must not inherit the claims of the rung above it.
 */
export function applyOfferStages(ctx, query, candidates, cap, mode, gapMs) {
    const byId = new Map(ctx.services.map((s) => [s.id, s]));
    const stepMs = ctx.slot_grid_min * MINUTE;
    const committed = [...(ctx.committed_starts ?? [])].sort((a, b) => a.start - b.start);
    const ledger = offerLedger(ctx);
    const decisions = [];
    const slots = [];
    for (const candidate of candidates) {
        const slot = candidate.slot;
        const verdict = judgeCandidate(mode, candidate.geometry, gapMs);
        const geometry = {
            score: verdict.score,
            abuts_before: verdict.abuts_before,
            abuts_after: verdict.abuts_after,
        };
        const decide = (kept, reason) => {
            decisions.push({ candidate, kept, reason, ...geometry });
        };
        if (slots.length >= cap) {
            decide(false, 'over_cap');
            continue;
        }
        const svc = byId.get(slot.service_id);
        // Pacing, before exclusivity: a slot the kitchen cannot take is not offered,
        // and it must not consume the resource's place in the packed book either —
        // dropping it here leaves the next slot on that lane free to be offered.
        const paceCap = svc?.max_starts_per_step ?? ctx.max_starts_per_step;
        // A party larger than the cap itself therefore fits in no step at all, and
        // is told so honestly rather than seated into a config that says the
        // kitchen cannot plate it.
        if (paceCap !== undefined) {
            const already = coversStartingIn(committed, Date.parse(slot.start), stepMs);
            if (already + query.party_size > paceCap) {
                decide(false, 'paced_out');
                continue;
            }
        }
        // The offer policy, also before exclusivity, and for pacing's reason. `open`
        // keeps everything, which is the branch every tenant walks today.
        if (!verdict.kept) {
            decide(false, verdict.reason);
            continue;
        }
        if (!ledger.admit(slot)) {
            decide(false, 'lane_taken');
            continue;
        }
        decide(true, verdict.reason);
        slots.push(slot);
    }
    return { decisions, slots, mode };
}
/**
 * The jigsaw order: closes a hole on both sides, then on one side, then an
 * anchor, then the rest — ties chronological, so the sort is total and the
 * answer is deterministic.
 *
 * It **reorders and never removes**, which is what keeps a rank a preference
 * rather than a fourth mode. And it runs after the cap, deliberately: the cap
 * is the engine's bound on the work one request may do (§5.5.5) and the
 * ledger's walk is chronological, so the set that was admitted is the honest
 * one; this decides what a surface reads out first.
 */
function rankByPacking(result) {
    const scores = new Map();
    for (const decision of result.decisions) {
        if (decision.kept)
            scores.set(decision.candidate.slot, decision.score);
    }
    return [...result.slots].sort((a, b) => (scores.get(b) ?? 0) - (scores.get(a) ?? 0) ||
        a.start.localeCompare(b.start) ||
        a.service_id.localeCompare(b.service_id) ||
        a.resource_id.localeCompare(b.resource_id) ||
        slotLanes(a).join(',').localeCompare(slotLanes(b).join(',')));
}
/**
 * **The shortest leftover gap that is still worth having**, in minutes.
 *
 * The tenant may name it (`offer_policy.min_useful_gap_min`); when she has not,
 * it is a fact about her own menu rather than a constant — **the shortest
 * occupied block any active scheduled item needs**, its duration plus its
 * resolved buffers. A gap that cannot hold the smallest thing she sells is a
 * gap she cannot sell, and a number derived from the catalog moves when the
 * catalog does instead of going stale in a config document.
 *
 * `slot_grid_min` when there are no active services at all, which is a tenant
 * with nothing to offer and therefore no candidates to judge.
 */
export function minUsefulGapMin(ctx) {
    const configured = ctx.offer_policy?.min_useful_gap_min;
    if (typeof configured === 'number' && configured > 0)
        return configured;
    let shortest = Number.POSITIVE_INFINITY;
    for (const service of ctx.services) {
        if (!service.active)
            continue;
        const buffers = buffersFor(ctx, service);
        const block = buffers.before_min + service.duration_min + buffers.after_min;
        if (block > 0 && block < shortest)
            shortest = block;
    }
    return Number.isFinite(shortest) ? shortest : ctx.slot_grid_min;
}
/**
 * Clip a requested window to what we are willing to answer about: never the
 * past, never beyond the horizon.
 */
export function clipWindow(ctx, dateFrom, dateTo) {
    const today = toIsoDay(ctx.timezone, ctx.now);
    const horizonEnd = addDays(today, ctx.horizon_days);
    const from = dayDistance(today, dateFrom) < 0 ? today : dateFrom;
    const to = dayDistance(dateTo, horizonEnd) < 0 ? horizonEnd : dateTo;
    return { from, to };
}
export function daysInQuery(ctx, query) {
    const { from, to } = clipWindow(ctx, query.date_from, query.date_to);
    return eachDay(from, to, ctx.horizon_days + 1);
}
//# sourceMappingURL=availability.js.map